import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { OBJECT_TYPES, FRAMELESS_GRID_CELL_SIZE_M, MAX_FRAMELESS_WIDTH_M, MAX_FRAMELESS_HEIGHT_M } from '../configuratorConstants';
import { distance } from '../configuratorUtils';

const useFramelessDrawing = ({
  svgRef,
  isActive,
  elementTypeToPlace,
  dispatch,
  screenToWorld,
  projectObjects,
  currentFloorId,
}) => {
  const isDrawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const currentSegmentStartRef = useRef(null);
  const lockedDirectionRef = useRef(null);
  const warningShownRef = useRef(false);

  useEffect(() => {
    const svgElement = svgRef.current;
    if (!svgElement || !isActive) {
      return;
    }

    const snapToGrid = (worldPoint) => {
      return {
        x: Math.round(worldPoint.x / FRAMELESS_GRID_CELL_SIZE_M) * FRAMELESS_GRID_CELL_SIZE_M,
        y: Math.round(worldPoint.y / FRAMELESS_GRID_CELL_SIZE_M) * FRAMELESS_GRID_CELL_SIZE_M,
      };
    };

    const handleMouseDown = (e) => {
      if (elementTypeToPlace !== OBJECT_TYPES.FRAMELESS_WALL_TOOL || e.button !== 0) {
        return;
      }
      
      // Не обрабатываем клики по другим элементам (стены, модули и т.д.)
      if (e.target.closest('[data-object-type]')) {
        return;
      }
      
      e.preventDefault();
      e.stopPropagation();

      const startPoint = snapToGrid(screenToWorld(e.clientX, e.clientY));
      
      isDrawingRef.current = true;
      lastPointRef.current = startPoint;
      currentSegmentStartRef.current = startPoint;
      lockedDirectionRef.current = null;
      warningShownRef.current = false;
      dispatch({
        type: 'START_DRAWING_WALL',
        payload: { startPoint, endPoint: startPoint },
      });
    };

    const handleMouseMove = (e) => {
      if (!isDrawingRef.current || elementTypeToPlace !== OBJECT_TYPES.FRAMELESS_WALL_TOOL) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      const currentPoint = snapToGrid(screenToWorld(e.clientX, e.clientY));
      
      // Убеждаемся, что currentSegmentStartRef установлен
      if (!currentSegmentStartRef.current) {
        currentSegmentStartRef.current = lastPointRef.current || currentPoint;
      }
      
      if (!lastPointRef.current) {
        lastPointRef.current = currentPoint;
        return;
      }

      const epsilon = 0.01;
      const dxFromLast = Math.abs(currentPoint.x - lastPointRef.current.x);
      const dyFromLast = Math.abs(currentPoint.y - lastPointRef.current.y);

      if (dxFromLast < epsilon && dyFromLast < epsilon) {
        dispatch({
          type: 'UPDATE_DRAWING_WALL',
          payload: { endPoint: currentPoint },
        });
        return;
      }

      const referencePoint =
        currentSegmentStartRef.current || lastPointRef.current || currentPoint;

      if (!lockedDirectionRef.current) {
        const deltaX = Math.abs(currentPoint.x - referencePoint.x);
        const deltaY = Math.abs(currentPoint.y - referencePoint.y);
        lockedDirectionRef.current =
          deltaX >= deltaY ? 'horizontal' : 'vertical';
      }

      const adjustedPoint =
        lockedDirectionRef.current === 'vertical'
          ? { x: referencePoint.x, y: currentPoint.y }
          : { x: currentPoint.x, y: referencePoint.y };

      // Проверяем проекции стены на оси X и Y - не должны превышать 6x4
      const startPoint = currentSegmentStartRef.current || referencePoint;
      const dx = Math.abs(adjustedPoint.x - startPoint.x);
      const dy = Math.abs(adjustedPoint.y - startPoint.y);
      
      let finalPoint = adjustedPoint;
      let needsLimit = false;
      
      if (dx > MAX_FRAMELESS_WIDTH_M) {
        // Ограничиваем по оси X
        const signX = adjustedPoint.x > startPoint.x ? 1 : -1;
        finalPoint = {
          ...finalPoint,
          x: startPoint.x + signX * MAX_FRAMELESS_WIDTH_M
        };
        needsLimit = true;
      }
      
      if (dy > MAX_FRAMELESS_HEIGHT_M) {
        // Ограничиваем по оси Y
        const signY = adjustedPoint.y > startPoint.y ? 1 : -1;
        finalPoint = {
          ...finalPoint,
          y: startPoint.y + signY * MAX_FRAMELESS_HEIGHT_M
        };
        needsLimit = true;
      }
      
      if (needsLimit) {
        // Показываем сообщение только один раз
        if (!warningShownRef.current) {
          setTimeout(() => toast.warn(`Максимально можна ставити ${MAX_FRAMELESS_WIDTH_M} на ${MAX_FRAMELESS_HEIGHT_M} сендвіча. Це максимум.`), 0);
          warningShownRef.current = true;
        }
        lastPointRef.current = finalPoint;
        
        // Обновляем preview линию с ограниченной точкой
        dispatch({
          type: 'UPDATE_DRAWING_WALL',
          payload: { 
            startPoint: startPoint,
            endPoint: finalPoint 
          },
        });
        return;
      } else {
        // Сбрасываем флаг предупреждения, если проекции в пределах нормы
        warningShownRef.current = false;
      }

      lastPointRef.current = adjustedPoint;
      
      // Обновляем preview линию
      dispatch({
        type: 'UPDATE_DRAWING_WALL',
        payload: { 
          startPoint: startPoint,
          endPoint: adjustedPoint 
        },
      });
    };

    const handleMouseUp = (e) => {
      if (!isDrawingRef.current || elementTypeToPlace !== OBJECT_TYPES.FRAMELESS_WALL_TOOL) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      if (lastPointRef.current && currentSegmentStartRef.current) {
        const dx = Math.abs(lastPointRef.current.x - currentSegmentStartRef.current.x);
        const dy = Math.abs(lastPointRef.current.y - currentSegmentStartRef.current.y);
        const epsilon = 0.01;

        // Если есть сегмент для создания
        if (dx > epsilon || dy > epsilon) {
            dispatch({
              type: 'ADD_WALL_SEGMENT',
              payload: {
                startPoint: currentSegmentStartRef.current,
                endPoint: lastPointRef.current,
              },
            });
            
            // ⚠️ ВАЖНО: Сразу завершаем рисование - ТОЛЬКО ПРЯМЫЕ ЛИНИИ!
            // Не даём продолжать рисовать "ступеньками"
            isDrawingRef.current = false;
            lastPointRef.current = null;
            currentSegmentStartRef.current = null;
            lockedDirectionRef.current = null;
            warningShownRef.current = false;
            
            dispatch({
              type: 'FINISH_DRAWING_WALL',
              payload: {},
            });
        }
      } else {
        // Если ничего не нарисовали - просто сбрасываем
        isDrawingRef.current = false;
        lastPointRef.current = null;
        currentSegmentStartRef.current = null;
        lockedDirectionRef.current = null;
        warningShownRef.current = false;
        
        dispatch({
          type: 'FINISH_DRAWING_WALL',
          payload: {},
        });
      }
    };

    const handleMouseLeave = () => {
      if (isDrawingRef.current) {
        // Завершаем рисование при выходе мыши
        if (lastPointRef.current && currentSegmentStartRef.current) {
          const dx = Math.abs(lastPointRef.current.x - currentSegmentStartRef.current.x);
          const dy = Math.abs(lastPointRef.current.y - currentSegmentStartRef.current.y);
          const epsilon = 0.01;

          if (dx > epsilon || dy > epsilon) {
              dispatch({
                type: 'ADD_WALL_SEGMENT',
                payload: {
                  startPoint: currentSegmentStartRef.current,
                  endPoint: lastPointRef.current,
                },
              });
          }
        }
        
        isDrawingRef.current = false;
        lastPointRef.current = null;
        currentSegmentStartRef.current = null;
        lockedDirectionRef.current = null;
        warningShownRef.current = false;
        
        dispatch({
          type: 'FINISH_DRAWING_WALL',
          payload: {},
        });
      }
    };

    svgElement.addEventListener('mousedown', handleMouseDown);
    svgElement.addEventListener('mousemove', handleMouseMove);
    svgElement.addEventListener('mouseup', handleMouseUp);
    svgElement.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      svgElement.removeEventListener('mousedown', handleMouseDown);
      svgElement.removeEventListener('mousemove', handleMouseMove);
      svgElement.removeEventListener('mouseup', handleMouseUp);
      svgElement.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [svgRef, isActive, elementTypeToPlace, dispatch, screenToWorld, projectObjects, currentFloorId]);

  // Сброс при смене режима или инструмента
  useEffect(() => {
    if (!isActive || elementTypeToPlace !== OBJECT_TYPES.FRAMELESS_WALL_TOOL) {
      isDrawingRef.current = false;
      lastPointRef.current = null;
      currentSegmentStartRef.current = null;
      lockedDirectionRef.current = null;
      warningShownRef.current = false;
    }
  }, [isActive, elementTypeToPlace]);
};

export default useFramelessDrawing;